import { motion } from "framer-motion";
import { useEffect } from "react";

type Props = {};

const SidekickPPPage = (props: Props) => {
	useEffect(() => {
		document.title = "Sidekick | Privacy Policy";
	}, []);

	return (
		<div className='flex relative overflow-y-scroll overflow-x-hidden gap-y-10 flex-col no-scrollbar items-center justify-center bg-gradient-to-br from-slate-800 via-slate-950 to-slate-800'>
			<motion.div
				className='max-w-4xl mx-auto p-6 bg-slate-800 shadow-md rounded-md my-8 text-slate-100'
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, ease: "easeOut" }}>
				<motion.h1
					className='text-3xl font-bold mb-4'
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.2 }}>
					Privacy Policy — Sidekick
				</motion.h1>

				<motion.div
					className='text-slate-200 font-semibold text-lg mb-8 space-y-1'
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.4 }}>
					<p>
						<strong>Effective:</strong> September 1, 2026
					</p>
					<p>
						<strong>Developer:</strong> Softwarify
					</p>
					<p>
						<strong>Contact:</strong> sio@softwarify.co
					</p>
				</motion.div>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
					<h2 className='text-2xl font-semibold mb-2'>No Account, No Name</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Sidekick never asks you to sign up. Tapping “Get started” creates an anonymous account identified only by a
						randomly generated ID. We do not collect your name, email address, phone number, date of birth or postal
						address, and we have no way to link your data to your identity.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
					<h2 className='text-2xl font-semibold mb-2'>Data We Collect</h2>
					<ul className='list-disc list-inside text-slate-200 font-semibold text-lg space-y-2'>
						<li>
							<strong>What you write:</strong> the short line you type about a meal, in your own words, stored exactly as
							you typed it (up to 280 characters) together with which of the three compounds — protein, fibre, fat — it
							covered and which foods were recognised in it.
						</li>
						<li>
							<strong>Your kitchen:</strong> which foods from our fixed food list you have said you keep, and when you
							last confirmed or ran out of them. There is no quantity, no expiry date and no inventory.
						</li>
						<li>
							<strong>Gaps you reached for:</strong> when you looked for a compound and had nothing on hand, so the app
							can build a shopping list.
						</li>
						<li>
							<strong>Your routine:</strong> the answers you give about your week — days out, meals per day, what is
							available where you work — plus your device's time zone, so reminders arrive at a sensible hour.
						</li>
						<li>
							<strong>Purchases:</strong> subscription status only. Payments are processed by Apple; we never see or
							receive your card or payment details.
						</li>
						<li>
							<strong>Anonymous usage and crash data:</strong> aggregate events such as screens opened and errors
							encountered, tied to the same anonymous ID and never to you personally.
						</li>
					</ul>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
					<h2 className='text-2xl font-semibold mb-2'>Photos</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						If you photograph your shelves or your food, the picture is sent once to our processing service, read for the
						foods it contains, and discarded. Photos are never stored on our servers, never attached to your account, and
						never used to train any model. Only the list of recognised foods is kept.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
					<h2 className='text-2xl font-semibold mb-2'>How We Use Data</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Everything above exists to do one thing: suggest something you already have that would round out what you are
						eating. We do not sell your data, we do not share it with advertisers, and we run no advertising in Sidekick.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
					<h2 className='text-2xl font-semibold mb-2'>Third Parties</h2>
					<ul className='list-disc list-inside text-slate-200 font-semibold text-lg space-y-1'>
						<li>
							Supabase — database and anonymous authentication (
							<a
								className='underline hover:text-slate-50'
								href='https://supabase.com/privacy'
								target='_blank'
								rel='noreferrer'>
								policy
							</a>
							)
						</li>
						<li>
							fal.ai — reads the sentence or photo you send and returns the foods in it (
							<a
								className='underline hover:text-slate-50'
								href='https://fal.ai/privacy'
								target='_blank'
								rel='noreferrer'>
								policy
							</a>
							)
						</li>
						<li>
							RevenueCat — subscription management (
							<a
								className='underline hover:text-slate-50'
								href='https://www.revenuecat.com/privacy'
								target='_blank'
								rel='noreferrer'>
								policy
							</a>
							)
						</li>
						<li>
							Apple — payment processing and delivery (
							<a
								className='underline hover:text-slate-50'
								href='https://www.apple.com/legal/privacy/'
								target='_blank'
								rel='noreferrer'>
								policy
							</a>
							)
						</li>
					</ul>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0 }}>
					<h2 className='text-2xl font-semibold mb-2'>Notifications</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						The evening reminder is scheduled on your device and is off until you turn it on. You can turn it off again at
						any time in Sidekick's settings, or in your device's own notification settings.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}>
					<h2 className='text-2xl font-semibold mb-2'>Where Your Data Lives</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						A copy is kept on your device so the app works without a connection, and a copy is stored on our Supabase
						project so it survives a reinstall. Server-side rows are protected by row-level security: an account can only
						ever read and write its own.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
					<h2 className='text-2xl font-semibold mb-2'>Deleting Everything</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Settings → Delete account removes your account and everything attached to it, immediately and permanently.
						Because the account has no email, it cannot be recovered afterwards. Uninstalling the app removes the copy on
						your device. You can also write to sio@softwarify.co, though there is nothing we can look up on your behalf —
						the account is anonymous by design.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}>
					<h2 className='text-2xl font-semibold mb-2'>Children</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Sidekick is not directed at children under 13 and we do not knowingly collect data from them.
					</p>
				</motion.section>

				<motion.section className='mb-6' initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
					<h2 className='text-2xl font-semibold mb-2'>Not Medical Advice</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						Sidekick is a general wellness app. It does not diagnose, treat or prevent any condition, it counts nothing,
						and nothing it suggests is a substitute for advice from a qualified health professional.
					</p>
				</motion.section>

				<motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
					<h2 className='text-2xl font-semibold mb-2'>Changes</h2>
					<p className='text-slate-200 font-semibold text-lg'>
						If this policy changes we will update the effective date above. Questions go to sio@softwarify.co or the
						contact form at{" "}
						<a className='underline hover:text-slate-50' href='https://softwarify.co/#contact'>
							softwarify.co
						</a>
						.
					</p>
				</motion.section>
			</motion.div>
		</div>
	);
};

export default SidekickPPPage;
